import { useState, useEffect } from 'react';
import api from '../services/api';
import { FieldDefinition } from '../contexts/FieldDefinitionsContext';

/**
 * Loads the Pending EDI Orders column registry (GET /field-definitions?scope=edi_orders).
 * Kept separate from the global FieldDefinitionsContext (orders-scoped) so EDI fields
 * never leak into the Dispatch/LC/Quote pickers. Module-cached so re-mounting is instant.
 *
 * These defs drive ONLY the drill-down TanTable's column picker + client-side
 * group/sort/show-hide — there is no generic query engine behind the edi_orders scope
 * (the drill runs a fixed bespoke query and returns every column in each row, so column
 * add/remove is pure client-side visibility, no refetch). `source_fieldname` is the row
 * accessor; the shared FieldDefinition type omits it, so we widen it here.
 */
export interface EdiFieldDefinition extends FieldDefinition {
  source_fieldname?: string | null;
  source_alias?: string | null;
}

let cache: EdiFieldDefinition[] | null = null;

export function useEdiOrdersFieldDefinitions(enabled: boolean = true) {
  const [fieldDefinitions, setFieldDefinitions] = useState<EdiFieldDefinition[]>(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (cache) {
      setFieldDefinitions(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get('/field-definitions', { params: { scope: 'edi_orders' } })
      .then((resp) => {
        if (cancelled) return;
        const defs = (resp.data?.data || []) as EdiFieldDefinition[];
        cache = defs;
        setFieldDefinitions(defs);
      })
      .catch((e) => {
        if (!cancelled) setError(e as Error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { fieldDefinitions, loading, error };
}

export default useEdiOrdersFieldDefinitions;
